import _ from 'underscore';
import React, { useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useCreatePromise } from '@bingads-webui-campaign-react/common-hooks';
import { SignupUserInfoDialog, useUserInfo } from '@bingads-webui-campaign-react/signup-user-info-form';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { useCreateUser } from './use-create-user';

const UserCreationContext = React.createContext({
  isCreatingUser: false,
  createUser: _.noop,
  createUserPromise: {},
});

export const useUserCreationContext = () => useContext(UserCreationContext);

export const UserCreationContextProvider = (props) => {
  const {
    children,
    devConfig,
    isCacheFlow,
  } = props;
  const serverContext = useContext(ServerContext);
  const { CurrentAccount, AuthType } = serverContext;
  const isChinaSignup = usePilotsValue('isCNMarketExpansionEnabled');

  const createUserPromiseRef = useRef();
  const [showUserInfoForm, userInfo, setUserInfo] = useUserInfo(serverContext);
  const [isCreatingUser, setIsCreatingUser] = useState(showUserInfoForm);
  const [, setHasError] = useState(false);

  const [, {
    createPromise,
    resolveLastCreatedPromise,
    rejectLastCreatedPromise,
  }] = useCreatePromise();

  createUserPromiseRef.current = createUserPromiseRef.current || createPromise();

  const instrumentation = useContext(InstrumentationContext);
  const uiActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.userCreation.create();
    }

    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const onUserCreationStart = useCallback(() => {
    setIsCreatingUser(true);
    createUserPromiseRef.current = createUserPromiseRef.current || createPromise();
    uiActivity.trace('[PMaxLiteSignup] Start creating user');
  }, [createPromise, uiActivity]);

  const onUserCreationComplete = useCallback((newServerContext) => {
    uiActivity.trace('[PMaxLiteSignup] User created');
    resolveLastCreatedPromise(newServerContext);
    setIsCreatingUser(false);
  }, [resolveLastCreatedPromise, uiActivity]);

  const onUserCreationError = useCallback((error) => {
    uiActivity.trace(`[PMaxLiteSignup] User creation failed. Error: ${JSON.stringify(error)}`);
    rejectLastCreatedPromise();
    createUserPromiseRef.current = undefined;
    setHasError(true);
    setIsCreatingUser(false);
  }, [rejectLastCreatedPromise, uiActivity]);

  const createUser = useCreateUser({
    devConfig,
    authType: AuthType,
    uiActivity,
    userInfo,
    isChinaSignup,
    onUserCreationStart,
    onUserCreationComplete,
    onUserCreationError,
  });

  useEffect(() => {
    if (CurrentAccount) {
      resolveLastCreatedPromise(serverContext);
      setIsCreatingUser(false);
    }
  }, [CurrentAccount, resolveLastCreatedPromise, serverContext, setIsCreatingUser]);

  // Cache flow pre-fills the website URL programmatically, so the URL-validation path that
  // normally triggers createUser() never runs. Create eagerly here to unblock the spinner.
  useEffect(() => {
    if (isCacheFlow && !CurrentAccount && !isCreatingUser) {
      createUser().catch(_.noop);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCacheFlow]);

  const onConfirmUserInfo = useCallback((newUserInfo) => {
    setUserInfo(newUserInfo);
    createUser(newUserInfo);
  }, [createUser, setUserInfo]);

  return (
    <UserCreationContext.Provider
      value={{
        isCreatingUser,
        createUser,
        createUserPromise: createUserPromiseRef.current,
      }}
    >
      {children}
      {
        showUserInfoForm &&
        <SignupUserInfoDialog
          defaultValue={userInfo}
          onConfirm={onConfirmUserInfo}
        />
      }
    </UserCreationContext.Provider>
  );
};

UserCreationContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  devConfig: PropTypes.shape({}),
  isCacheFlow: PropTypes.bool,
};

UserCreationContextProvider.defaultProps = {
  devConfig: {},
  isCacheFlow: false,
};
